import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  ShieldAlert, ArrowLeft, Check, X, Hourglass, CalendarClock, Users, Building2, Info,
} from "lucide-react";
import {
  useBlockCatalogue, ownersForStage, codesForStage, ownerMeta, openBlocks,
  blockAgeDays, promiseState, blockHeadline,
} from "@/lib/blockCatalogue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * BlockModal v2 — "Who are you waiting on?" (BLOCKING_REDESIGN_PLAN.md §6).
 *
 * Two steps instead of the old sub-team→code drilldown: pick the OWNER (the
 * stakeholder who must act), then the reason from that owner's short list for
 * this stage. The sub-team dimension is gone — the owner carries that meaning.
 *
 * An item can carry several blocks at once, so this modal doubles as the place
 * to see and clear the ones already open.
 *
 * Props:
 *   item            line item being blocked (needs .stage, .client, .blocks)
 *   partySuggestions string[] — party names used before (supplier/lab/vendor)
 *   onClose         () => void
 *   onAdd           (payload) => Promise|void
 *   onClear         (blockId, clearNote) => Promise|void
 */
export default function BlockModalV2({ item, partySuggestions = [], onClose, onAdd, onClear }) {
  const { catalogue } = useBlockCatalogue();
  const stage = item?.stage || "";
  const itemId = item?.id || "";

  const [owner, setOwner] = useState("");
  const [code, setCode] = useState("");
  const [freeForm, setFreeForm] = useState(false);   // "not listed" escape hatch
  const [note, setNote] = useState("");
  const [party, setParty] = useState("");
  const [promise, setPromise] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [clearing, setClearing] = useState("");

  // Waiting-on tagging — unchanged mechanism, now stored per block.
  const [mentionables, setMentionables] = useState([]);
  const [waitingOn, setWaitingOn] = useState([]);
  const [waitQuery, setWaitQuery] = useState("");
  const [demandNextAction, setDemandNextAction] = useState(true);

  const om = useMemo(() => ownerMeta(catalogue, owner), [catalogue, owner]);
  const owners = useMemo(() => ownersForStage(catalogue, stage), [catalogue, stage]);
  const external = owners.filter((o) => o.type === "external");
  const internal = owners.filter((o) => o.type === "internal");
  const codes = useMemo(() => codesForStage(catalogue, stage, owner), [catalogue, stage, owner]);
  const open = useMemo(() => openBlocks(item), [item]);

  // Keyed on the id, NOT the object: StagePage re-derives `item` from the items
  // array on every refresh, and depending on the object would wipe what the user
  // is typing the moment the board polls.
  useEffect(() => {
    if (!itemId) return;
    setOwner(""); setCode(""); setFreeForm(false); setNote("");
    setParty(""); setPromise(""); setWaitingOn([]); setWaitQuery(""); setDemandNextAction(true);
  }, [itemId]);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/users/mentionables`);
        if (!cancelled) setMentionables(Array.isArray(res.data) ? res.data : []);
      } catch (e) { /* silent — tagging simply unavailable */ }
    })();
    return () => { cancelled = true; };
  }, [itemId]);

  // Party defaults follow the registry: the customer is the item's own client,
  // a department names itself, a supplier/lab/vendor must be typed.
  const pickOwner = (key) => {
    const meta = ownerMeta(catalogue, key);
    setOwner(key); setCode(""); setFreeForm(false);
    if (meta?.party_source === "client") setParty(item?.client || "");
    else if (meta?.party_source === "department") setParty(meta.label);
    else setParty("");
    setPromise("");
  };

  const waitMatches = useMemo(() => {
    const q = waitQuery.trim().toLowerCase();
    if (!q) return [];
    const chosen = new Set(waitingOn.map((u) => u.id));
    return mentionables
      .filter((u) => !chosen.has(u.id) && ((u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)))
      .slice(0, 6);
  }, [waitQuery, mentionables, waitingOn]);

  const partyNeeded = om?.party_source === "autocomplete";
  const promiseNeeded = !!om?.promise_required;
  const canSubmit = !!owner && (freeForm ? note.trim().length >= 3 : !!code)
    && (!partyNeeded || party.trim().length > 0)
    && (!promiseNeeded || !!promise) && !submitting;

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onAdd({
        owner,
        code: freeForm ? "" : code,
        note: note.trim(),
        party: party.trim(),
        expected_unblock_at: promise,
        waiting_on_user_ids: waitingOn.map((u) => u.id),
        demand_next_action: demandNextAction,
      });
    } finally { setSubmitting(false); }
  }, [canSubmit, owner, code, freeForm, note, party, promise, waitingOn, demandNextAction, onAdd]);

  if (!item) return null;

  const chipBase = "rl-pill inline-flex flex-col items-start gap-0.5 text-left px-3 py-2 rounded-lg border transition-colors min-w-[132px] flex-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500";
  const chipCls = (o, active) => active
    ? (o.type === "external"
        ? "bg-amber-500/15 border-amber-500 text-amber-700 dark:text-amber-300"
        : "bg-slate-500/15 border-slate-500 text-slate-700 dark:text-slate-200")
    : (o.type === "external"
        ? "bg-amber-500/[0.07] border-amber-500/40 text-[hsl(var(--foreground))] hover:border-amber-500/80 hover:bg-amber-500/15"
        : "bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:border-slate-500/60 hover:bg-slate-500/5");

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[720px] max-w-[95vw] max-h-[88vh] overflow-y-auto"
        data-testid="block-modal-v2"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <ShieldAlert className="w-5 h-5" /> {open.length ? "Blocks on this item" : "What's holding this up?"}
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-[hsl(var(--foreground))]">{item.item_description}</span>
            &nbsp;(<span className="font-mono-data text-xs font-semibold"><span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">PO</span>{item.po_number}</span>)
            &nbsp;— {stage}.
          </DialogDescription>
        </DialogHeader>

        {/* ─── Blocks already open — each cleared on its own ─── */}
        {open.length > 0 && (
          <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 divide-y divide-[hsl(var(--border))]" data-testid="open-blocks-list">
            {open.map((b) => {
              const ps = promiseState(b);
              const days = blockAgeDays(b);
              const ext = b.owner_type === "external";
              return (
                <div key={b.id} className="flex items-start gap-3 px-3 py-2.5">
                  <span className={`font-mono-data text-[10px] font-bold px-1.5 py-0.5 rounded border shrink-0 mt-0.5 whitespace-nowrap ${
                    !b.owner
                      ? "bg-[hsl(var(--muted))]/60 text-[hsl(var(--muted-foreground))] border-dashed border-[hsl(var(--muted-foreground))]/50"
                      : ext ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40"
                            : "bg-slate-500/15 text-slate-700 dark:text-slate-200 border-slate-500/40"}`}>
                    {b.owner || "SET OWNER"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[hsl(var(--foreground))] break-words">
                      {blockHeadline(catalogue, b)}
                      {b.code && <span className="font-mono-data text-[10px] text-[hsl(var(--muted-foreground))] ml-1.5">{b.code}</span>}
                    </div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-2 flex-wrap mt-0.5">
                      {b.party && <span>{b.party}</span>}
                      {days !== null && <span>· {days === 0 ? "today" : `${days}d`}</span>}
                      {b.expected_unblock_at && (
                        <span className={ps === "overdue" ? "text-red-500 font-semibold" : ""}>
                          · promised {b.expected_unblock_at}{ps === "overdue" ? " — overdue" : ""}
                        </span>
                      )}
                    </div>
                    {b.note && b.note !== blockHeadline(catalogue, b) && (
                      <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 break-words">{b.note}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={clearing === b.id}
                    onClick={async () => { setClearing(b.id); try { await onClear(b.id, ""); } finally { setClearing(""); } }}
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-emerald-500/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40 transition-colors"
                    data-testid={`clear-block-${b.id}`}
                  >
                    <Check className="w-3 h-3" /> {clearing === b.id ? "Clearing…" : "Cleared"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {/* ─── Step 1 — who are we waiting on? ─── */}
          {!owner && (
            <div className="space-y-3" data-testid="owner-grid">
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Building2 className="w-3 h-3 text-amber-600" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">Waiting on — outside</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {external.map((o) => (
                    <button key={o.key} type="button" onClick={() => pickOwner(o.key)} className={`${chipBase} ${chipCls(o, false)}`} data-testid={`owner-${o.key}`}>
                      <span className="text-sm font-semibold">{o.label}</span>
                      <span className="text-[10px] opacity-70 leading-tight">{o.definition}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Users className="w-3 h-3 text-slate-500" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Waiting on — inside</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {internal.map((o) => (
                    <button key={o.key} type="button" onClick={() => pickOwner(o.key)} className={`${chipBase} ${chipCls(o, false)}`} data-testid={`owner-${o.key}`}>
                      <span className="text-sm font-semibold">{o.label}</span>
                      <span className="text-[10px] opacity-70 leading-tight">{o.definition}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── Step 2 — the reason, from that owner's short list ─── */}
          {owner && (
            <>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { setOwner(""); setCode(""); setFreeForm(false); }}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[hsl(var(--foreground))]/30 transition-colors"
                  data-testid="owner-back">
                  <ArrowLeft className="w-3 h-3" /> Change
                </button>
                <span className={`text-sm font-semibold ${om?.type === "external" ? "text-amber-700 dark:text-amber-300" : "text-[hsl(var(--foreground))]"}`}>
                  Waiting on {om?.label}
                </span>
              </div>

              <div className="rounded-lg border border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))] overflow-hidden" data-testid="reason-list">
                {codes.map((c) => (
                  <button key={c.code} type="button"
                    onClick={() => { setCode(c.code); setFreeForm(false); }}
                    className={`w-full text-left px-3 py-2 flex items-start gap-2.5 transition-colors ${code === c.code && !freeForm ? "bg-red-500/10" : "hover:bg-[hsl(var(--muted))]/50"}`}
                    data-testid={`reason-${c.code}`}>
                    <span className={`mt-1 w-3 h-3 rounded-full border shrink-0 ${code === c.code && !freeForm ? "bg-red-500 border-red-500" : "border-[hsl(var(--muted-foreground))]/50"}`} />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-[hsl(var(--foreground))]">{c.reason}</span>
                      <span className="block text-[11px] text-[hsl(var(--muted-foreground))] leading-snug">{c.guideline}</span>
                    </span>
                    <span className="font-mono-data text-[10px] text-[hsl(var(--muted-foreground))] ml-auto shrink-0 pt-0.5">{c.code}</span>
                  </button>
                ))}
                <button type="button" onClick={() => { setFreeForm(true); setCode(""); }}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors ${freeForm ? "bg-amber-500/10" : "hover:bg-[hsl(var(--muted))]/50"}`}
                  data-testid="reason-freeform">
                  <span className={`w-3 h-3 rounded-full border shrink-0 ${freeForm ? "bg-amber-500 border-amber-500" : "border-[hsl(var(--muted-foreground))]/50 border-dashed"}`} />
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-400">None of these — I'll describe it</span>
                </button>
              </div>

              {/* Party + promise date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1.5 block">
                    Who exactly{partyNeeded && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  {om?.party_source === "client" ? (
                    <div className="h-9 px-3 flex items-center rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-sm font-medium" data-testid="party-locked">
                      {party || "—"}
                      <span className="text-[10px] text-[hsl(var(--muted-foreground))] ml-2">the client on this PO</span>
                    </div>
                  ) : (
                    <>
                      <input
                        value={party}
                        onChange={(e) => setParty(e.target.value)}
                        list="rl-party-suggestions"
                        placeholder={om?.party_hint || om?.label || "Name"}
                        className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-red-500"
                        data-testid="party-input"
                      />
                      <datalist id="rl-party-suggestions">
                        {partySuggestions.map((p) => <option key={p} value={p} />)}
                      </datalist>
                    </>
                  )}
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1.5 block">
                    Expected unblock{promiseNeeded && <span className="text-red-500 ml-0.5">*</span>}
                    <span className="normal-case font-normal tracking-normal opacity-70 ml-1">{promiseNeeded ? "" : "(optional)"}</span>
                  </label>
                  <div className="relative">
                    <CalendarClock className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] pointer-events-none" />
                    <input
                      type="date" value={promise} onChange={(e) => setPromise(e.target.value)}
                      className="w-full h-9 pl-8 pr-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-red-500"
                      data-testid="promise-input"
                    />
                  </div>
                  <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                    {promiseNeeded
                      ? `When has ${party || om?.label?.toLowerCase() || "they"} committed to resolve this?`
                      : "Add a date if one was promised — it drives the overdue flag."}
                  </p>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1.5 block">
                  {freeForm ? <>Describe the block<span className="text-red-500 ml-0.5">*</span></> : <>Detail <span className="normal-case font-normal opacity-70">(optional)</span></>}
                </label>
                <textarea
                  value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                  placeholder={freeForm ? "Explain what is holding this up." : "Serial numbers, quantities, what was promised…"}
                  className={`w-full px-3 py-2 text-sm rounded-md bg-[hsl(var(--muted))] border text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 resize-none ${freeForm ? "border-amber-500/40 focus:ring-amber-500" : "border-[hsl(var(--border))] focus:ring-red-500"}`}
                  data-testid="block-note-input"
                />
              </div>

              {/* Waiting on — per-block user tagging */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1.5 block">
                  Tag a colleague <span className="font-normal normal-case tracking-normal">(optional — they get notified)</span>
                </label>
                <div className="flex items-center gap-1.5 flex-wrap rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 px-2 py-1.5">
                  {waitingOn.map((u) => (
                    <span key={u.id} className="inline-flex items-center gap-1 text-[11.5px] font-medium text-orange-700 dark:text-orange-400 bg-orange-500/5 border border-orange-500/40 rounded-full pl-2 pr-1 py-px rl-pill">
                      {u.name}
                      <button type="button" onClick={() => setWaitingOn((l) => l.filter((x) => x.id !== u.id))} className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-orange-500/15" aria-label={`Remove ${u.name}`}>
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                  <input value={waitQuery} onChange={(e) => setWaitQuery(e.target.value)}
                    placeholder={waitingOn.length ? "Add another…" : "Type a name…"}
                    className="flex-1 min-w-[120px] bg-transparent text-sm focus:outline-none placeholder:text-[hsl(var(--muted-foreground))]/50"
                    data-testid="block-waiting-input" />
                </div>
                {waitMatches.length > 0 && (
                  <div className="mt-1 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] shadow-md overflow-hidden">
                    {waitMatches.map((u) => (
                      <button key={u.id} type="button"
                        onClick={() => { setWaitingOn((l) => [...l, u]); setWaitQuery(""); }}
                        className="flex items-center gap-2 w-full px-2.5 py-1.5 text-left text-sm hover:bg-[hsl(var(--muted))]/50 transition-colors">
                        <span className="font-medium">{u.name}</span>
                        <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{u.role}</span>
                      </button>
                    ))}
                  </div>
                )}
                {waitingOn.length > 0 && (
                  <label className="mt-2 inline-flex items-center gap-2 text-[12px] text-[hsl(var(--foreground))]/80 cursor-pointer select-none">
                    <input type="checkbox" checked={demandNextAction} onChange={(e) => setDemandNextAction(e.target.checked)} className="w-3.5 h-3.5 accent-orange-600" />
                    <span className="inline-flex items-center gap-1"><Hourglass className="w-3 h-3 text-amber-600" /> Ask them to specify the next action</span>
                  </label>
                )}
              </div>
            </>
          )}

          {!owner && open.length === 0 && (
            <p className="flex items-start gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))]">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              Pick whoever has to act for this to move again. An item can wait on more than one — add a block for each.
            </p>
          )}

          <DialogFooter>
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-md text-sm font-medium bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors"
              data-testid="block-cancel-button">
              {open.length ? "Done" : "Cancel"}
            </button>
            {owner && (
              <button type="submit" disabled={!canSubmit}
                className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="block-confirm-button">
                {submitting ? "Blocking…" : `Block — waiting on ${om?.label || ""}`}
              </button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
